// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppState } from '@/store';
import { selectStepsProgresses } from '../../feature';

// Models
import { ContractStepProgress, SettlementStepProgress } from '@/models/propertyProgress.model';
import { StepStatus, StepType } from '@/models/step.model';
import { OrderStatus } from '@/models/order.model';

// Components
import {
  ResetStepButton,
  StepCard,
  StyledContractTag,
  StyledList,
  StyledListRow,
  StyledRow,
  StyledText,
} from '../../components';

// Helpers
import { finalStatuses } from '../../helpers';

type TSettlementProgressContainerProps = {
  hideResetButton?: boolean;
};

const SettlementProgressContainer = ({ hideResetButton = false }: TSettlementProgressContainerProps) => {
  const { t } = useTranslation('property');
  const stepsProgresses = useAppState(selectStepsProgresses);

  const settlementProgress = stepsProgresses?.[StepType.SETTLEMENT] as SettlementStepProgress;
  const contractProgress = stepsProgresses?.[StepType.CONTRACT] as ContractStepProgress;
  const contractCompletedWithSelfService =
    !!contractProgress?.data_uploaded_at && contractProgress?.status === StepStatus.COMPLETED;

  // Signers are hidden if step completed without order
  const showSigners =
    (settlementProgress?.status !== StepStatus.COMPLETED ||
      !settlementProgress?.data_uploaded_at ||
      settlementProgress?.order?.status === OrderStatus.COMPLETED) &&
    !contractCompletedWithSelfService;

  if (!settlementProgress) return null;

  return (
    <StepCard title={t('step_settlement')} status={settlementProgress.status}>
      <StyledRow>
        {!hideResetButton && finalStatuses.includes(settlementProgress.status) && (
          <ResetStepButton
            propertyId={settlementProgress?.property_id}
            stepName={StepType.SETTLEMENT}
            shouldResetStepData={false}
          />
        )}
      </StyledRow>
      {showSigners && (
        <StyledList>
          {settlementProgress?.signers?.map((signer, idx) => {
            return (
              <StyledListRow key={signer.signed_at}>
                <StyledText>
                  {t(`contract_${signer?.signer_type}_count`, { count: idx + 1 })}
                  {` (${signer?.signer_name}): `}
                </StyledText>
                <StyledContractTag $status={signer?.signing_status}>
                  {signer?.signing_status ? t(`signing_status_${signer?.signing_status}`) : '-'}
                </StyledContractTag>
              </StyledListRow>
            );
          })}
        </StyledList>
      )}
    </StepCard>
  );
};

export default SettlementProgressContainer;
